import React, { useState, useEffect } from 'react';
import { Globe, Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@vaiu/ui/card";
import { Label } from "@vaiu/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@vaiu/ui/select";
import { toast } from "sonner";
import { i18n, SUPPORTED_LANGUAGES } from '@/utils/i18n';

const LanguageSelector: React.FC = () => {
  const [currentLanguage, setCurrentLanguage] = useState(i18n.getLanguage());
  const [isUpdating, setIsUpdating] = useState(false);

  // Listen for language changes from other parts of the app
  useEffect(() => {
    const handleLanguageChange = (event: CustomEvent) => {
      setCurrentLanguage(event.detail);
    };

    window.addEventListener('languageChanged', handleLanguageChange as EventListener);

    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange as EventListener);
    };
  }, []);

  const handleLanguageChange = async (newLanguage: keyof typeof SUPPORTED_LANGUAGES) => {
    if (newLanguage === currentLanguage) return;

    setIsUpdating(true);
    try {
      // Update i18n system
      i18n.setLanguage(newLanguage);
      setCurrentLanguage(newLanguage);

      // Show success message
      toast.success(i18n.__('vaiu.profile_page.language_updated'));

      // Force re-render of the page by dispatching a custom event
      window.dispatchEvent(new CustomEvent('profileUpdate'));
    } catch (error) {
      console.error('Failed to update language:', error);
      toast.error(i18n.__('vaiu.errors.try_again_later'));
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center space-x-2">
          <Globe className="w-5 h-5" />
          <CardTitle>{i18n.__('vaiu.profile_page.language')}</CardTitle>
        </div>
        <CardDescription>
          {i18n.__('vaiu.profile_page.language_description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <Label htmlFor="language-select">
              {i18n.__('vaiu.profile_page.language')}
            </Label>
            <Select
              value={currentLanguage}
              onValueChange={handleLanguageChange}
              disabled={isUpdating}
            >
              <SelectTrigger className="w-full mt-2">
                <SelectValue placeholder={i18n.__('vaiu.profile_page.language')} />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SUPPORTED_LANGUAGES).map(([code, name]) => (
                  <SelectItem key={code} value={code}>
                    <div className="flex items-center justify-between w-full">
                      <span>{name}</span>
                      {code === currentLanguage && (
                        <Check className="w-4 h-4 ml-2 text-green-600" />
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Language info */}
          <div className="text-sm text-muted-foreground">
            <div className="flex items-center space-x-2">
              <Globe className="w-4 h-4" />
              <span>
                {i18n.__('vaiu.profile_page.language')}: {SUPPORTED_LANGUAGES[currentLanguage]}
              </span>
            </div>
          </div>

          {/* Manual refresh note */}
          <div className="text-xs text-muted-foreground p-2 bg-blue-50 rounded border border-blue-200">
            💡 Language changes take effect immediately. Some parts of the interface may require a page refresh to update completely.
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default LanguageSelector; 